import {
  gachaEvmAddressSchema,
  getGachaPreferredToken,
} from "@anaconda/core/gacha/evm-chains"
import {
  gachaEthPaymentChainIdSchema,
  getGachaEthPaymentChain,
} from "@anaconda/core/gacha/evm-eth-payment"
import { gachaEvmHashSchema } from "@anaconda/core/gacha/evm-purchase"
import { action, atom } from "@reatom/core"
import { z } from "zod"

const submissionSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().min(1),
  walletAddress: gachaEvmAddressSchema,
  chainId: gachaEthPaymentChainIdSchema,
  hash: gachaEvmHashSchema.optional(),
})
export type GachaEthSubmission = z.infer<typeof submissionSchema>
export type GachaEthRecoveryStatus = "pending" | "unknown" | "confirmed"
export class GachaEthPaymentRecoveryError extends Error {
  constructor(
    readonly status: GachaEthRecoveryStatus,
    readonly submission: GachaEthSubmission,
  ) {
    super(
      status === "confirmed"
        ? `Your previous ETH transaction is confirmed. Check your ${getGachaPreferredToken(getGachaEthPaymentChain(submission.chainId)).symbol} balance before retrying.`
        : status === "pending"
          ? "ETH conversion is still pending. Check its status; do not convert again."
          : "ETH conversion submission is uncertain. Check your wallet before retrying.",
    )
  }
}
const storageKey = "gacha.eth-payment.submissions.v1"
type SubmissionStorage = Pick<Storage, "getItem" | "setItem">

export function createGachaEthPaymentTracking(
  name: string,
  getStorage?: () => SubmissionStorage | undefined,
) {
  const records = atom<GachaEthSubmission[]>([], `${name}.records`)
  const storage = () => {
    try {
      return getStorage?.()
    } catch {
      return undefined
    }
  }
  const load = () => {
    let stored: string | null | undefined
    try {
      stored = storage()?.getItem(storageKey)
    } catch {
      return
    }
    if (stored) records.set(z.array(submissionSchema).parse(JSON.parse(stored)))
  }
  const persist = () => {
    if (!getStorage) return
    const target = storage()
    if (!target)
      throw new Error(
        "Browser storage is required to safely track ETH conversions",
      )
    target.setItem(storageKey, JSON.stringify(records()))
  }
  const get = action((walletAddress: string, chainId: number) => {
    load()
    return records().find(
      (record) =>
        record.chainId === chainId &&
        record.walletAddress.toLowerCase() === walletAddress.toLowerCase(),
    )
  }, `${name}.get`)
  const clear = action((submission: GachaEthSubmission) => {
    load()
    records.set((current) =>
      current.filter((record) => record.id !== submission.id),
    )
    persist()
  }, `${name}.clear`)
  const begin = action((submission: GachaEthSubmission) => {
    const previous = get(submission.walletAddress, submission.chainId)
    if (previous)
      throw new GachaEthPaymentRecoveryError(
        previous.hash ? "pending" : "unknown",
        previous,
      )
    records.set((current) => [...current, submission])
    try {
      persist()
    } catch {
      records.set((current) =>
        current.filter((record) => record !== submission),
      )
      throw new Error(
        "Could not save ETH conversion tracking. No conversion was requested. Enable browser storage and retry.",
      )
    }
  }, `${name}.begin`)
  const broadcast = action((submission: GachaEthSubmission) => {
    load()
    records.set((current) =>
      current.map((record) =>
        record.id === submission.id ? submission : record,
      ),
    )
    persist()
  }, `${name}.broadcast`)
  const acknowledgeUnknown = action(
    (walletAddress: string, chainId: number, id: string) => {
      const submission = get(walletAddress, chainId)
      if (!submission) return
      if (submission.hash || submission.id !== id)
        throw new GachaEthPaymentRecoveryError(
          submission.hash ? "pending" : "unknown",
          submission,
        )
      clear(submission)
    },
    `${name}.acknowledgeUnknown`,
  )
  return { get, begin, broadcast, clear, acknowledgeUnknown }
}
export type GachaEthPaymentTracking = ReturnType<
  typeof createGachaEthPaymentTracking
>
export const gachaEthPaymentTracking = createGachaEthPaymentTracking(
  "gacha.ethPayments",
  () => (typeof window === "undefined" ? undefined : window.localStorage),
)
